import type { ColorFragment } from '@graphql/fragments/ColorFragment.graphql'
import type { LiteralUnion } from 'type-fest'

/**
 * Brand colors available for use in components.
 * These correspond to CSS custom properties in _colors.scss (e.g., --step-cyanora).
 * Used for background colors, text colors, and icon colors.
 *
 * Note: This list is manually maintained. When adding new colors to _colors.scss,
 * add them here if they should be available as component props.
 */
export const BRAND_COLORS = [
    // Primary brand colors
    'step-cyanora',
    'step-cyanora-down',
    'step-future-fuscia',
    'step-future-fuscia-down',
    'step-coal-mine-canary',
    'step-coal-mine-canary-down',
    'step-amethyst',
    'step-springtime',

    // Neutrals - dark
    'step-black',
    'step-black-v2',
    'step-midnight',
    'step-before-midnight',
    'step-right-before-midnight',

    // Neutrals - light
    'step-white',
    'step-off-white',
    'step-cold-steel',

    // Status
    'step-angry',

    // Gradients
    'step-linear-gradient-cyanora-to-future-fuscia',
    'step-vertical-gradient-cyanora-to-future-fuscia',
    'step-vertical-gradient-cyanora-to-violet',
    'step-diagonal-gradient-midnight-haze-to-black',
] as const

export type BrandColor = LiteralUnion<(typeof BRAND_COLORS)[number], string>

export function asColor(something: unknown) {
    return (something as ColorFragment)?.label
        ? (something as ColorFragment)
        : null
}

export function asColorLabel(something: unknown) {
    return asColor(something)?.label as BrandColor | undefined
}

export function asCSSVariable(something: unknown) {
    return `var(--${asColorLabel(something)})`
}
